import React from "react";

interface OtpInputProps {
  otp: string[];
  setOtp: (otp: string[]) => void;
  onSubmit: (otp: string) => void;
  validate?: (otp: string) => { isValid: boolean; message?: string };
  setError?: (message: string) => void;
  clearErrors?: () => void;
  error?: string;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  otp,
  setOtp,
  onSubmit,
  validate,
  setError,
  clearErrors,
  error,
  disabled,
}) => {
  const inputs = React.useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return; // Only numbers allowed

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    // Focus next input if value is entered
    if (value && index < otp.length - 1) {
      inputs.current[index + 1]?.focus();
    }

    // Validate and submit when OTP is complete
    const fullOtp = updatedOtp.join("");
    if (!updatedOtp.includes("") && fullOtp.length === otp.length) {
      if (validate) {
        const { isValid, message } = validate(fullOtp);
        if (isValid) {
          clearErrors?.();
          onSubmit(fullOtp);
        } else {
          setError?.(message || "Invalid OTP");
        }
      } else {
        onSubmit(fullOtp);
      }
    } else if (clearErrors && fullOtp.length < otp.length) {
      clearErrors();
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pastedData) || pastedData.length > otp.length) return;

    const pastedOtp = pastedData.split("").slice(0, otp.length);
    const updatedOtp = [...otp];

    for (let i = 0; i < pastedOtp.length && index + i < otp.length; i++) {
      updatedOtp[index + i] = pastedOtp[i];
    }

    setOtp(updatedOtp);

    const lastFilledIndex = Math.min(
      index + pastedOtp.length - 1,
      otp.length - 1
    );
    inputs.current[lastFilledIndex]?.focus();

    // Validate and submit if OTP is complete
    const fullOtp = updatedOtp.join("");
    if (!updatedOtp.includes("") && fullOtp.length === otp.length) {
      if (validate) {
        const { isValid, message } = validate(fullOtp);
        if (isValid) {
          clearErrors?.();
          onSubmit(fullOtp);
        } else {
          setError?.(message || "Invalid OTP");
        }
      } else {
        onSubmit(fullOtp);
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center gap-2 mt-4">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onPaste={(e) => handlePaste(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`w-12 h-12 text-center text-xl border rounded-lg focus:outline-none focus:ring-2 transition-all ${
              error ? "border-red-500" : "border-gray-300"
            } ${
              disabled
                ? "bg-gray-100 cursor-not-allowed"
                : "focus:ring-blue-500"
            }`}
            disabled={disabled}
          />
        ))}
      </div>
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
    </div>
  );
};

export default OtpInput;
